import { Divider } from "@brexhq/metal-divider";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { PortableText } from "@portabletext/react";
import React, { PropsWithChildren } from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { buildLinkPath } from "@brexhq/next/src/components/pageBuilder/utils";
import { BynderAsset } from "@brexhq/next/src/components/shared/BynderAsset/BynderAsset";
import { ImageBorderRadiusWrapper } from "@brexhq/next/src/components/shared/BynderAsset/Image/BynderImage";
import { Link } from "@brexhq/next/src/components/shared/Link/Link";
import MediaCaption from "@brexhq/next/src/components/shared/MediaCaption";
import Quotes from "@brexhq/next/src/components/shared/Quotes/Quotes";
import {
  BodyL,
  BodyM,
  BodyS,
  FlechaXS,
  SubtitleL,
  SubtitleS,
  TitleS,
  TitleSReg,
  TitleXS,
  TitleXXS,
} from "@brexhq/shared/components/Typography/Components";
import { BaseTextProps } from "@brexhq/shared/components/Typography/types";
import {
  StandardThemeProperties,
  CardThemeProperties,
} from "@brexhq/shared/types/ModuleTheme";
import { desktop, tablet, mobile } from "@brexhq/shared/types/SpacingScale";
import { getYouTubeEmbedUrl } from "@brexhq/shared/utils/getYouTubeEmbedUrl";

import { InlineTable } from "../../building-blocks/ComparisonTable/components/InlineTable";
import { TagReference } from "../../deprecated/BlockContent/HelpCenterBlockContent";
import BynderVideo from "../BynderAsset/Video/BynderVideo";

import CopyLibraryItem from "./CopyLibraryItem";

interface Props {
  blocks: any[];
  theme: StandardThemeProperties | CardThemeProperties;
  /** set type size for heading and body simultaneously*/
  typeScale: "L" | "M" | "S";
  colorOverwrite?: "primary" | "secondary";
  // analytics name that will passed down to any links in this block
  component: string;
}

const IframeVideoContainer = styled.div({
  /**
   * ref: https://flaviocopes.com/responsive-youtube-videos
   * This code makes the iframe responsive.
   * YouTube videos need a specific height pixel value to render properly.
   * The aspect ratio of YouTube videos is 16:9 (9 is 56.25% of 16).
   */
  maxWidth: "100%",
  height: 0,
  position: "relative",
  paddingBottom: "56.25%",
  overflow: "hidden",
});

const IframeVideo = styled.iframe({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
});

const Dropcap = styled.span`
  display: block;
  &:first-letter {
    font-family: "Bronzea";
    font-size: 500%;
    line-height: 90%;
    letter-spacing: 5px;
    float: left;
  }
`;

// Used as the container renderer for block content. Ensures that the last
// child does not have any bottom margin.
const BlockContentContainer = styled.div`
  max-width: ${({ theme }) => theme.maxWidths.textContent};

  & > *:last-child {
    margin-bottom: 0;
  }
`;

export type typeScale = "L" | "M" | "S";

type StyleProps = React.FC<PropsWithChildren<Partial<BaseTextProps>>>;

/**
 * For more info on how this component works, checkout
 * https://www.sanity.io/docs/portable-text-to-react#available-components
 */
export default function BlockRenderer({
  blocks,
  theme,
  typeScale,
  colorOverwrite,
  component,
}: Props) {
  if (component === "") {
    // Please be kind to our analytics friends and set a helpful component name!
    // This allows us to track link clicks back to their component.
    throw new Error("A valid component prop is required");
  }

  const libraryTheme = useTheme();
  const { textPrimary, textSecondary } = theme;
  let overWriteStyle;
  if (colorOverwrite) {
    overWriteStyle = colorOverwrite === "primary" ? textPrimary : textSecondary;
  }

  let HeadingLargeStyle: StyleProps;
  let HeadingSmallStyle: StyleProps;
  let HeadingExtraSmallStyle: StyleProps;
  let BodyLargeStyle: StyleProps;
  let BodySmallStyle: StyleProps;
  let DisplayFlechaStyle: StyleProps;
  let DisplayInterStyle: StyleProps;

  let margin = {
    LRG: [0, 0, desktop.med, 0],
    MED: [0, 0, tablet.med, 0],
    SML: [0, 0, mobile.sml, 0],
  };
  switch (typeScale) {
    case "L":
      HeadingLargeStyle = TitleS;
      HeadingSmallStyle = TitleXS;
      HeadingExtraSmallStyle = TitleXXS;
      BodyLargeStyle = BodyL;
      BodySmallStyle = BodyM;
      DisplayFlechaStyle = FlechaXS;
      DisplayInterStyle = TitleSReg;
      break;
    case "M":
      HeadingLargeStyle = TitleXS;
      HeadingSmallStyle = SubtitleL;
      HeadingExtraSmallStyle = TitleXXS;
      BodyLargeStyle = BodyM;
      BodySmallStyle = BodyS;
      DisplayFlechaStyle = FlechaXS;
      DisplayInterStyle = TitleSReg;
      margin = {
        LRG: [0, 0, desktop.sml, 0],
        MED: [0, 0, tablet.sml, 0],
        SML: [0, 0, mobile.sml, 0],
      };
      break;
    case "S":
      HeadingLargeStyle = SubtitleL;
      HeadingSmallStyle = SubtitleS;
      HeadingExtraSmallStyle = TitleXXS;
      BodyLargeStyle = BodyS;
      BodySmallStyle = BodyS;
      DisplayFlechaStyle = FlechaXS;
      DisplayInterStyle = TitleSReg;
      margin = {
        LRG: [0, 0, desktop.xsml, 0],
        MED: [0, 0, tablet.xsml, 0],
        SML: [0, 0, mobile.xsml, 0],
      };
      break;
  }

  return (
    <BlockContentContainer>
      <PortableText
        value={blocks}
        components={{
          marks: {
            indentQuote: ({ children }) => {
              return <Quotes indent>{children}</Quotes>;
            },
            lineQuote: ({ children }) => {
              return <Quotes>{children}</Quotes>;
            },
            link: ({ value, children }) => {
              const to = buildLinkPath({ link: value?.link });
              return to ? (
                <Link
                  component={component}
                  modifier="inline"
                  to={to}
                  noFollow={value?.link?.noFollow}
                  label={children?.[0]}
                  color={overWriteStyle ?? textPrimary}
                />
              ) : (
                children?.[0]
              );
            },
            hashId: ({ value, children }) => (
              <span id={value?.link?.hashId}>{children}</span>
            ),
          },
          list: {
            bullet: ({ children }) => (
              <BodyLargeStyle
                as="ul"
                color={overWriteStyle ?? textPrimary}
                margin={margin}
              >
                {children}
              </BodyLargeStyle>
            ),
            number: ({ children }) => (
              <BodyLargeStyle
                as="ol"
                color={overWriteStyle ?? textPrimary}
                margin={margin}
              >
                {children}
              </BodyLargeStyle>
            ),
          },

          listItem: ({ children }) => (
            <BodyLargeStyle
              as="li"
              color={overWriteStyle ?? textSecondary}
              margin={{
                LRG: [0, 0, desktop.xsml, 0],
                MED: [0, 0, tablet.xsml, 0],
                SML: [0, 0, mobile.xsml, 0],
              }}
            >
              {children}
            </BodyLargeStyle>
          ),

          block: {
            dropcap: ({ children }) => (
              <BodyLargeStyle color={textSecondary} margin={margin}>
                <Dropcap>{children}</Dropcap>
              </BodyLargeStyle>
            ),
            heading: ({ children }) => (
              <HeadingLargeStyle
                as="h2"
                color={overWriteStyle ?? textPrimary}
                margin={margin}
              >
                {children}
              </HeadingLargeStyle>
            ),
            "heading small": ({ children }) => (
              <HeadingSmallStyle
                as="h3"
                color={overWriteStyle ?? textPrimary}
                margin={margin}
              >
                {children}
              </HeadingSmallStyle>
            ),
            "heading extra small": ({ children }) => (
              <HeadingExtraSmallStyle
                as="h4"
                color={overWriteStyle ?? textPrimary}
                margin={margin}
              >
                {children}
              </HeadingExtraSmallStyle>
            ),
            "body small": ({ children }) => (
              <BodySmallStyle
                color={overWriteStyle ?? textPrimary}
                margin={margin}
              >
                {children}
              </BodySmallStyle>
            ),
            "display inter": ({ children }) => (
              <DisplayInterStyle
                as="h2"
                color={overWriteStyle ?? textSecondary}
                margin={margin}
              >
                {children}
              </DisplayInterStyle>
            ),
            "display flecha": ({ children }) => (
              <DisplayFlechaStyle
                color={overWriteStyle ?? textSecondary}
                margin={margin}
              >
                {children}
              </DisplayFlechaStyle>
            ),
            // Body large
            normal: ({ children }) => (
              <BodyLargeStyle
                color={overWriteStyle ?? textSecondary}
                margin={margin}
              >
                {children}
              </BodyLargeStyle>
            ),
          },
          types: {
            video({ value: { video, caption, border } }) {
              return (
                <figure style={{ marginBottom: libraryTheme.spacing.LRG.lrg }}>
                  <BynderVideo
                    {...video}
                    border={border}
                    controls
                    controlsList="nodownload"
                    disablePictureInPicture={false}
                    loop
                  />
                  {caption && (
                    <MediaCaption
                      caption={caption}
                      textColor={overWriteStyle ?? textSecondary}
                    />
                  )}
                </figure>
              );
            },
            iframeVideo({ value: { url, caption } }) {
              return (
                <figure style={{ marginBottom: libraryTheme.spacing.LRG.lrg }}>
                  <IframeVideoContainer>
                    <IframeVideo
                      allowFullScreen
                      loading="lazy"
                      title="YouTube video"
                      src={getYouTubeEmbedUrl(url)}
                      width="100%"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      data-chromatic="ignore"
                    />
                  </IframeVideoContainer>

                  {caption && (
                    <MediaCaption
                      caption={caption}
                      textColor={overWriteStyle ?? textSecondary}
                    />
                  )}
                </figure>
              );
            },
            imageWithCaption({ value: { asset, altText, caption } }) {
              return (
                <figure style={{ marginBottom: libraryTheme.spacing.LRG.lrg }}>
                  <ImageBorderRadiusWrapper mediaStyle={asset.mediaStyle}>
                    <BynderAsset
                      asset={asset.media}
                      altTextOverride={altText}
                      loopVideo={!asset.disableVideoLoop}
                      showVideoControls={asset.showVideoControls}
                    />
                  </ImageBorderRadiusWrapper>
                  {caption && (
                    <MediaCaption
                      caption={caption}
                      textColor={overWriteStyle ?? textSecondary}
                    />
                  )}
                </figure>
              );
            },
            dividerLine: ({ value: { style } }) => {
              switch (style) {
                case "dotted": {
                  return (
                    <Container
                      margin={{
                        LRG: ["lrg", 0],
                        MED: ["lrg", 0],
                        SML: ["lrg", 0],
                      }}
                    >
                      <Divider borderStyle="dashed" />
                    </Container>
                  );
                }
                /**
                 * Default value will always be a line break
                 * but as a precaution we want to match against undefined
                 * */
                case "line break":
                default: {
                  return (
                    <Container
                      margin={{
                        LRG: ["lrg", 0],
                        MED: ["lrg", 0],
                        SML: ["lrg", 0],
                      }}
                    >
                      <Divider color={libraryTheme.colors.content.disabled} />
                    </Container>
                  );
                }
              }
            },
            copyLibraryReference: ({ value }) => {
              return <CopyLibraryItem value={value} />;
            },
            inlineTable: ({ value }) => {
              return <InlineTable value={value} />;
            },
            tagReference: ({ value }) => {
              return <TagReference variant="body" {...value.tagItem} />;
            },
          },
        }}
      />
    </BlockContentContainer>
  );
}
